/**
 * Canonical AppResponse envelope helpers for company-portal.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * core-api reports BUSINESS failures inside an HTTP 200 body:
 *
 *   { errorCode: '0',    message: null,          details: null,  data: {...} }  // success
 *   { errorCode: '1027', message: 'INVALID ...', details: '...', data: null }   // failure
 *
 * Because the failure rides inside a 2xx response, `.unwrap()` on an RTK Query
 * trigger does NOT reject on it — a page that unwraps and destructures only
 * "fails" by accident, when a `data: null` body happens to throw. Every correct
 * check in this repo used to be an unexported module-local copy, so each new
 * page re-invented (and re-broke) it. This module is the single exported copy.
 *
 * AppResponse's default constructor sets errorCode "0" on success (verified in
 * core-api AppResponse.java), so `errorCode === '0'` is a safe success
 * predicate. Do NOT branch on errorCode '1030': that is the generic
 * CUSTOM_ERROR code shared by every custom error (invalid reset token, mail
 * failure, ...). Match a specific business outcome on `message`.
 *
 * WHAT THE GLOBAL LAYER ALREADY REPORTS — DO NOT DOUBLE-TOAST
 * -----------------------------------------------------------
 * `baseQueryWithReAuth` in src/services/api-slice.ts already calls
 * displayNotification, globally, for:
 *
 *   - HTTP 400 and HTTP 500 transport errors
 *       → "Something went wrong!" / "Please try this action later or contact
 *         BrickLynk support."
 *   - any body carrying errorCode '1030'
 *       → title = body.message, description = body.details
 *
 * It also handles the 401 refresh/logout dance. That behaviour is deliberate
 * and stays byte-identical; this module is purely additive for call sites.
 *
 * The consequence for a call site: a BODY_FAILURE whose errorCode is '1030'
 * has ALREADY been shown to the user. Adding a second displayNotification for
 * it stacks two toasts for one failure. The band that is genuinely SILENT
 * today is a 2xx body with a non-'0', non-'1030' errorCode (e.g. 1027) and a
 * transport error whose status is neither 400 nor 500 (FETCH_ERROR, 403, 404,
 * a timeout) — that is what a call site must make visible, either with an
 * inline `role="alert"` region or displayNotification.
 *
 * What every call site MUST do regardless of toasting: on anything that is not
 * SUCCESS, do not show a success toast, do not navigate away, do not close the
 * drawer/modal, do not persist anything, and leave the user's input intact.
 */

/** The AppResponse envelope fields a call site is allowed to reason about. */
export type AppResponsePayload = {
  errorCode?: string;
  message?: string | null;
  details?: string | null;
};

/** The three ways an awaited, NON-unwrapped mutation trigger can land. */
export const MutationResult = {
  /** 2xx with a body-level success (`errorCode === '0'`). */
  SUCCESS: 'SUCCESS',
  /** The request was answered, but the envelope says the operation failed. */
  BODY_FAILURE: 'BODY_FAILURE',
  /** No usable answer at all: network error, timeout, or a non-2xx status. */
  TRANSPORT_FAILURE: 'TRANSPORT_FAILURE',
} as const;

export type MutationResultKind =
  (typeof MutationResult)[keyof typeof MutationResult];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Normalise ANY awaited mutation result to the AppResponse envelope it carries.
 *
 * Handles the shapes core-api and RTK Query actually produce:
 *   { data: { errorCode, message, details, data } }  → that body
 *   { error: { status, data: {...} } }               → the error body
 *   { data: '' } / { data: null }                    → {} (failure replies)
 *   anything else                                    → {}
 *
 * It ALWAYS returns an object, so `payload.message` is safe to read without a
 * guard. It never throws.
 */
export const extractResponsePayload = (
  response: unknown
): AppResponsePayload => {
  if (!isRecord(response)) return {};

  // A failure reply can carry `data: ''` or `data: null`; neither is an
  // envelope, so fall through rather than handing a string back to the caller.
  if (isRecord(response.data)) return response.data as AppResponsePayload;

  if (isRecord(response.error) && isRecord(response.error.data)) {
    return response.error.data as AppResponsePayload;
  }

  return {};
};

/**
 * True only for a body-level success. `data: ''`, `data: null`, a non-'0'
 * errorCode and the `{ error }` transport shape are all false.
 */
export const isSuccess = (response: unknown): boolean => {
  if (!isRecord(response)) return false;
  if (!isRecord(response.data)) return false;
  return (response.data as AppResponsePayload).errorCode === '0';
};

/**
 * Classify the raw awaited result of a NON-unwrapped RTK Query mutation
 * trigger. Fails closed: anything that is not provably a body-level success is
 * a failure, so a new backend error code can never read as success.
 */
export const classifyMutationResult = (
  response: unknown
): MutationResultKind => {
  // Not an object at all (undefined from an aborted call, say) — treat as
  // "no usable answer", the same bucket as a network error.
  if (!isRecord(response)) return MutationResult.TRANSPORT_FAILURE;

  // RTK Query resolves a non-unwrapped trigger to `{ error }` for BOTH a raw
  // network failure and a non-2xx status. First-class shape, not an edge case.
  if ('error' in response && response.error !== undefined) {
    return MutationResult.TRANSPORT_FAILURE;
  }

  return isSuccess(response)
    ? MutationResult.SUCCESS
    : MutationResult.BODY_FAILURE;
};
